/* iter55f82 — ManuscriptBody.
 *
 * Extracted from EditorPanel.jsx to shrink the parent below the 600-line
 * threshold and keep the manuscript-rendering concern in one file. Pure
 * presentational: every mutation goes back to the parent via callbacks
 * so the parent still owns state (chapter, suggestions, settings,
 * toneBusy, streaming).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Moon, Sun, Zap } from 'lucide-react';
import { fmtTokens } from '@/lib/tokenEstimate';
import { StreamingProse } from './StreamingProse';
import { ClaudeSuggestion } from './ClaudeSuggestion';
import { InlineToneButton } from './EditorTonePresets';
import { EmptyState } from './EmptyState';
import HighlightedParagraph from './HighlightedParagraph';
import { FONT_BY_KEY, clampSize as clampManuscriptSize, PARA_SPACING_OPTIONS } from './ManuscriptToolbar';
import { BlankChapterCanvas } from '../BlankChapterCanvas';
import CoachHint from '@/components/coach/CoachHint';
import { editorHint } from '@/components/coach/coachHints';
import { chapterLabel } from '@/lib/chapterLabel';
import { usePW } from '@/lib/store';
import useCrossParagraphSelection from './useCrossParagraphSelection';
import useSuggestionOverlays from './useSuggestionOverlays';
import { runTour } from '@/lib/tours';
import { startGlow, stopGlow } from '@/components/GlowNudge';
import { FindBar } from './FindBar';
import { readColumnWidth, COLUMN_WIDTH_EVENT } from '@/components/dialogs/SettingsDialog';
import { ManuscriptContextMenu } from './ManuscriptContextMenu';

function useColumnWidth() {
  const [width, setWidth] = useState(() => readColumnWidth());
  useEffect(() => {
    const handler = (e) => setWidth(e.detail || 'full');
    window.addEventListener(COLUMN_WIDTH_EVENT, handler);
    return () => window.removeEventListener(COLUMN_WIDTH_EVENT, handler);
  }, []);
  return width;
}

export default function ManuscriptBody({
  scrollAreaRef,
  ch,
  paragraphs,
  paragraphsEditable,
  isEmpty,
  isStreamingThis,
  streamingText,
  settings,
  activeMode,
  canMutate,
  canGenerate,
  canAiWriteChapters,
  setActiveMode,
  blankCanvasRef,
  blankHover,
  setBlankHover,
  onGenerateChapter,
  saveParagraphEdit,
  crossParaDelete,
  undoCrossParaDelete,
  onDirty,
  onDirtyClear,
  mergeParagraphUp,
  pendingSuggestions,
  onAnchorClick,
  suggestion,
  onAcceptSuggestion,
  onRejectSuggestion,
  toneBusy,
  isDemo,
  askToneShift,
  enoughForTone,
  toneCost,
  remaining,
  aiShare = null,
  onCoachActionsClick = null,   // ← new: fires when author clicks ⚡ AI Actions on the Beat hint
}) {
  const { project } = usePW();
  const columnWidth = useColumnWidth();
  // iter55g26 — Cross-paragraph selection (Cmd/Ctrl+A + spanning
  // copy/cut). Each paragraph is its own contentEditable node so
  // browsers otherwise cap Select-All to a single paragraph.
  const proseRef = useRef(null);
  // Track last focused contentEditable so undo/redo can refocus it
  const lastFocusedRef = useRef(null);

  // Suppresses onBlur → saveParagraphEdit during a cross-paragraph delete.
  // crossParaDelete calls onBeforeCrossParaDelete BEFORE sel.removeAllRanges()
  // so the flag is true when the blur events fire synchronously.
  const crossParaDeleteActiveRef = useRef(false);

  // Suppresses onBlur → saveParagraphEdit during a manual save.
  // Without this, active.blur() in EditorPanel's loom:manual-save handler
  // fires onBlur AFTER we've already called saveParagraphEdit explicitly here,
  // causing a second save with potentially stale/wrong state that can dump
  // the paragraph text at the bottom of the chapter.
  const manualSaveActiveRef = useRef(false);

  const handleCrossParaDelete = useCallback((firstIdx, mergedText, lastIdx) => {
    const done = () => { crossParaDeleteActiveRef.current = false; };
    const p = crossParaDelete?.(firstIdx, mergedText, lastIdx);
    if (p && typeof p.then === 'function') p.then(done, done);
    else done();
  }, [crossParaDelete]);

  useCrossParagraphSelection(proseRef, {
    onBeforeCrossParaDelete: () => { crossParaDeleteActiveRef.current = true; },
    onCrossParaDelete:      handleCrossParaDelete,
    onUndoCrossParaDelete:  undoCrossParaDelete,
  });

  // When the toolbar Save button fires loom:manual-save, this handler runs
  // BEFORE EditorPanel's handler (child effects register first), so we can
  // read the focused paragraph's live DOM text and call saveParagraphEdit
  // explicitly.  We then set manualSaveActiveRef so the onBlur that fires
  // from EditorPanel's subsequent active.blur() is suppressed — preventing
  // the double-save race that dumps text at the bottom of the chapter.
  useEffect(() => {
    const onManualSave = () => {
      const ae = document.activeElement;
      if (!ae || !proseRef.current) return;
      if (!proseRef.current.contains(ae) || ae.contentEditable !== 'true') return;

      // Identify which paragraph index is focused by matching the DOM node.
      const nodes = proseRef.current.querySelectorAll('[contenteditable="true"]');
      let focusedIdx = -1;
      nodes.forEach((node, idx) => { if (node === ae) focusedIdx = idx; });
      if (focusedIdx < 0 || focusedIdx >= paragraphs.length) return;

      // Explicitly save the live DOM text NOW, before the blur fires.
      const liveText = ae.innerText || ae.textContent || '';
      saveParagraphEdit(focusedIdx, liveText);

      // Suppress the onBlur-triggered save that EditorPanel's active.blur() 
      // is about to cause — it would race with the call above.
      manualSaveActiveRef.current = true;
      setTimeout(() => { manualSaveActiveRef.current = false; }, 300);
    };
    window.addEventListener('loom:manual-save', onManualSave);
    return () => window.removeEventListener('loom:manual-save', onManualSave);
  }, [paragraphs, saveParagraphEdit]);

  // ── Page Up / Page Down + Ctrl+Home / Ctrl+End ────────────────────────────
  // Scroll the manuscript viewport by one page (PageUp/Down) or jump to the
  // very first / last paragraph (Ctrl/Cmd + Home/End).
  //
  // FIX: The listener is on `document` (capture phase) rather than the scroll
  // area element. When the writer presses PageDown several times the browser
  // may scroll the focused paragraph off-screen and blur it, shifting focus
  // to document.body.  At that point an area-scoped listener never fires.
  // Mounting on document ensures the keys always work as long as the prose
  // container is on screen, while the guard at the top limits handling to
  // when focus is inside the area OR the area itself, and ignores events
  // that originate from modals, sidebars, or other panels.
  useEffect(() => {
    const area = scrollAreaRef?.current;
    if (!area) return;
    const onKey = (ev) => {
      // Only handle PageUp/Down and Ctrl+Home/End
      const isPageKey = ev.key === 'PageDown' || ev.key === 'PageUp';
      const isCtrlHomeEnd = (ev.ctrlKey || ev.metaKey) && (ev.key === 'Home' || ev.key === 'End');
      const isPlainHomeEnd = !ev.ctrlKey && !ev.metaKey && (ev.key === 'Home' || ev.key === 'End');
      if (!isPageKey && !isCtrlHomeEnd && !isPlainHomeEnd) return;

      // Only handle when focus is inside the scroll area, is the area itself,
      // or is on document.body / document.documentElement (i.e. no explicit focus).
      const ae = document.activeElement;
      const focusInArea = area.contains(ae) || ae === area;
      const noExplicitFocus = ae === document.body || ae === document.documentElement || ae == null;
      if (!focusInArea && !noExplicitFocus) return;

      // For plain Home/End only act when focus isn't inside a contentEditable
      // (the browser handles cursor-to-line-start/end in that case).
      const inEditable = ae?.contentEditable === 'true';
      if (isPlainHomeEnd && inEditable) return;

      if (ev.key === 'PageDown') {
        ev.preventDefault();
        area.scrollBy({ top: area.clientHeight * 0.9, behavior: 'smooth' });
      } else if (ev.key === 'PageUp') {
        ev.preventDefault();
        area.scrollBy({ top: -area.clientHeight * 0.9, behavior: 'smooth' });
      } else if (isCtrlHomeEnd && ev.key === 'Home') {
        ev.preventDefault();
        area.scrollTo({ top: 0, behavior: 'smooth' });
        const first = proseRef.current?.querySelector('[contenteditable="true"]');
        if (first) {
          first.focus();
          try {
            const range = document.createRange();
            range.setStart(first, 0);
            range.collapse(true);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          } catch { /* ignore */ }
        }
      } else if (isCtrlHomeEnd && ev.key === 'End') {
        ev.preventDefault();
        area.scrollTo({ top: area.scrollHeight, behavior: 'smooth' });
        const paras = proseRef.current?.querySelectorAll('[contenteditable="true"]');
        const last = paras?.length ? paras[paras.length - 1] : null;
        if (last) {
          last.focus();
          try {
            const range = document.createRange();
            range.selectNodeContents(last);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          } catch { /* ignore */ }
        }
      } else if (isPlainHomeEnd && ev.key === 'Home') {
        ev.preventDefault();
        area.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (isPlainHomeEnd && ev.key === 'End') {
        ev.preventDefault();
        area.scrollTo({ top: area.scrollHeight, behavior: 'smooth' });
      }
    };
    // Attach to document in capture phase — fires for all keyboard events
    // regardless of which element holds focus, so we can gate on focusInArea.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [scrollAreaRef, proseRef]);

  // ── Select-All flash badge ────────────────────────────────────────────────
  const [selectBadge, setSelectBadge] = useState(null); // null | number
  useEffect(() => {
    const onSelectAll = (e) => {
      setSelectBadge(e.detail.count);
      const t = setTimeout(() => setSelectBadge(null), 1800);
      return () => clearTimeout(t);
    };
    window.addEventListener('loom:select-all-para', onSelectAll);
    return () => window.removeEventListener('loom:select-all-para', onSelectAll);
  }, []);

  // Undo / Redo via loom custom events (fired by EditMenu so focus has
  // already moved away from the paragraph nodes — we restore it first)
  useEffect(() => {
    const onUndo = () => {
      const target = lastFocusedRef.current || proseRef.current?.querySelector('[contenteditable="true"]');
      if (target) { target.focus(); document.execCommand('undo'); }
    };
    const onRedo = () => {
      const target = lastFocusedRef.current || proseRef.current?.querySelector('[contenteditable="true"]');
      if (target) { target.focus(); document.execCommand('redo'); }
    };
    window.addEventListener('loom:edit-undo', onUndo);
    window.addEventListener('loom:edit-redo', onRedo);
    return () => {
      window.removeEventListener('loom:edit-undo', onUndo);
      window.removeEventListener('loom:edit-redo', onRedo);
    };
  }, []);
  // iter55g35 — Non-invasive inline overlays for anchored suggestions
  // over the editable paragraph nodes. Read-only paragraphs already
  // render inline <mark> highlights via HighlightedParagraph, so we
  // gate the overlay layer on `paragraphsEditable`.
  const overlays = useSuggestionOverlays(proseRef, pendingSuggestions, !!paragraphsEditable);
  // iter55h — Rich hover popover for anchored suggestions. Overlays
  // keep `pointer-events-none` (must, to preserve cross-paragraph text
  // selection), so we detect hover via a container-level mousemove
  // that hit-tests the cursor against each rect. The popover itself is
  // also pointer-events-none so hovering it doesn't retrigger.
  const [hover, setHover] = useState(null);
  // iter-storystart · Sep-2026 — Nudge a brand-new, still-empty
  // chapter with a one-time spotlight on the writing frame.
  useEffect(() => {
    if (!isEmpty || !paragraphsEditable || !ch) return undefined;
    const t = setTimeout(() => runTour('author-begin-story'), 700);
    startGlow('author-begin-story', 'manuscript-blank-canvas');
    return () => { clearTimeout(t); stopGlow('author-begin-story'); };
  }, [isEmpty, paragraphsEditable, ch]);
  // First real content committed on a chapter that started empty →
  // point the writer at "Chat with AI" for a "what's next" suggestion.
  const handleBlankCanvasSave = (idx, text) => {
    saveParagraphEdit(idx, text);
    if ((text || '').trim().length > 0) {
      setTimeout(() => runTour('author-ai-next-step'), 600);
      startGlow('author-ai-next-step', 'activity-chat-btn');
    }
  };
  useEffect(() => {
    if (!paragraphsEditable) return undefined;
    const container = proseRef.current;
    if (!container) return undefined;
    const onMove = (ev) => {
      if (!overlays.length) { setHover(null); return; }
      const cRect = container.getBoundingClientRect();
      const x = ev.clientX - cRect.left + container.scrollLeft;
      const y = ev.clientY - cRect.top  + container.scrollTop;
      let match = null;
      for (const o of overlays) {
        for (const r of o.rects) {
          if (x >= r.left && x <= r.left + r.width &&
              y >= r.top  && y <= r.top  + r.height) {
            match = { s: o.suggestion, rect: r };
            break;
          }
        }
        if (match) break;
      }
      setHover((cur) => {
        if (!match) return null;
        if (cur && cur.s.id === match.s.id) return cur;
        return match;
      });
    };
    const onLeave = () => setHover(null);
    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseleave', onLeave);
    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
    };
  }, [overlays, paragraphsEditable]);
  // iter55g36 — Cmd/Ctrl+J (next) / Shift+Cmd/Ctrl+J (previous) jumps
  // the reviewer to the next unresolved suggestion overlay and pulses
  // it for 1.2 s. Cycles through pendingSuggestions in DOM order (as
  // returned by the overlay hook).
  const [pulseId, setPulseId] = useState(null);
  const cursorRef = useRef(-1);
  useEffect(() => {
    if (!paragraphsEditable) return undefined;
    const onKey = (ev) => {
      const isJump = (ev.metaKey || ev.ctrlKey) && !ev.altKey && (ev.key === 'j' || ev.key === 'J');
      if (!isJump) return;
      if (!overlays.length) return;
      ev.preventDefault();
      ev.stopPropagation();
      // Group overlays by suggestion.id so multi-line anchors count as
      // ONE stop even though they produce multiple rectangles.
      const seen = new Set();
      const stops = [];
      for (const o of overlays) {
        if (seen.has(o.suggestion.id)) continue;
        seen.add(o.suggestion.id);
        stops.push(o);
      }
      if (!stops.length) return;
      const dir = ev.shiftKey ? -1 : 1;
      cursorRef.current = (cursorRef.current + dir + stops.length) % stops.length;
      const target = stops[cursorRef.current];
      const container = proseRef.current;
      const scroller = scrollAreaRef?.current;
      if (target && target.rects.length && scroller && container) {
        const rect = target.rects[0];
        // Scroll the manuscript-scroll-area so the rectangle sits
        // roughly a third down from the top of the viewport.
        const containerTop = container.offsetTop || 0;
        const desired = containerTop + rect.top - (scroller.clientHeight / 3);
        scroller.scrollTo({ top: Math.max(0, desired), behavior: 'smooth' });
      }
      setPulseId(target?.suggestion?.id || null);
      // Auto-clear pulse after animation completes.
      window.setTimeout(() => {
        setPulseId((cur) => (cur === target?.suggestion?.id ? null : cur));
      }, 1400);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlays, paragraphsEditable, scrollAreaRef]);
  const chapterWords = (paragraphs || []).reduce(
    (n, p) => n + ((p.text || '').trim().split(/\s+/).filter(Boolean).length),
    0,
  );
  const suggestionCount = (pendingSuggestions || []).length;
  const hint = editorHint({
    chapterWords,
    isEmpty,
    isStreaming: isStreamingThis,
    hasPendingSuggestions: suggestionCount > 0,
    suggestionCount,
    canGenerate,
    canAiWriteChapters,
    activeMode,
    isDemo,
  });

  return (
    <div ref={scrollAreaRef}
        className="relative flex-1 overflow-auto scroll-thin bg-canvas-workspace"
        data-testid="manuscript-scroll-area"
        onFocusCapture={(e) => {
          if (e.target?.contentEditable === 'true') lastFocusedRef.current = e.target;
        }}
        onClick={() => {
          if (canMutate && activeMode !== 'draft') setActiveMode('draft');
        }}>
      {/* Right-click context menu — clipboard + tool shortcuts */}
      <ManuscriptContextMenu scrollAreaRef={scrollAreaRef} askToneShift={askToneShift} />
      {/* Find / Find-Replace toolbar */}
      <FindBar containerRef={proseRef} />
      {/* iter55g45 — Suggestion minimap (right rail). Renders one tick
          per anchored overlay at its proportional position within the
          manuscript. Click a tick → jump-scroll + pulse (same
          behaviour as Cmd/Ctrl+J), or hover to see the suggestion
          preview. Only shows when the editable path is active and
          there is at least one anchored overlay. */}
      {paragraphsEditable && overlays.length > 0 && (() => {
        const proseEl = proseRef.current;
        const totalH = proseEl?.scrollHeight || 1;
        // Dedupe by suggestion id — one tick per suggestion, positioned
        // at its FIRST rect (top-most line).
        const seen = new Map();
        for (const o of overlays) {
          if (!seen.has(o.suggestion.id)) seen.set(o.suggestion.id, o);
        }
        const ticks = Array.from(seen.values());
        return (
          <div className="pointer-events-none sticky top-0 float-right h-full w-3 pr-1"
               style={{ marginRight: 4 }}
               data-testid="manuscript-minimap-rail"
               aria-hidden="true"
          >
            <div className="relative h-full w-full">
              {ticks.map((o) => {
                const pct = Math.min(0.995, Math.max(0.005, (o.rects[0]?.top || 0) / totalH));
                const s = o.suggestion;
                const isEdit = s.kind === 'edit';
                const label = (isEdit ? 'Edit: ' : 'Comment: ') + (s.anchor_text || '').slice(0, 60);
                return (
                  <button
                    key={o.suggestion.id}
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onAnchorClick?.(s, ev);
                      // Also scroll to it (matches Cmd/Ctrl+J UX).
                      const scroller = scrollAreaRef?.current;
                      const proseTop = proseRef.current?.offsetTop || 0;
                      const desired  = proseTop + (o.rects[0]?.top || 0) - (scroller?.clientHeight || 0) / 3;
                      scroller?.scrollTo({ top: Math.max(0, desired), behavior: 'smooth' });
                    }}
                    title={label}
                    data-testid={`manuscript-minimap-tick-${s.id}`}
                    className={
                      'pointer-events-auto absolute right-0 h-1.5 w-2 cursor-pointer rounded-sm transition-transform hover:scale-125 '
                      + (isEdit ? 'bg-amber-500/85 hover:bg-amber-600'
                                : 'bg-sky-500/85 hover:bg-sky-600')
                    }
                    style={{ top: `${pct * 100}%` }}
                  />
                );
              })}
            </div>
          </div>
        );
      })()}
      <div className="w-full px-6 pb-32 pt-10">
        {/* ── Floating paper card (InDesign document-within-workspace) ── */}
        <div
          className="canvas-paper rounded-sm shadow-paper px-12 py-10 min-h-[calc(100vh-120px)] mx-auto"
          style={columnWidth !== 'full' ? { maxWidth: `${columnWidth}px` } : undefined}
        >
        <CoachHint
          {...hint}
          side="left"
          align="start"
          testId="coach-manuscript"
          onActionsClick={onCoachActionsClick ? () => onCoachActionsClick(hint) : undefined}
        >
          <div className="mb-6 flex items-center gap-3 text-[13px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="manuscript-meta">
            <span>Part Two</span><span>•</span><span>Chapter {ch.number}</span><span>•</span>
            <span className="text-primary capitalize">{ch.status}</span>
            {typeof aiShare === 'number' && aiShare > 0 && (
              <>
                <span>•</span>
                <span
                  data-testid="manuscript-ai-share-legend"
                  className={
                    aiShare >= 66 ? 'text-warning'
                    : aiShare >= 34 ? 'text-success'
                    : 'text-primary'
                  }
                  title={`Editing-record shows ${aiShare}% of the recorded actions on this chapter are AI-assisted. The left-edge tint on every paragraph reflects this share.`}
                >
                  {aiShare}% AI-assist · left edge tint
                </span>
              </>
            )}
          </div>
        </CoachHint>
        <h1
          className="text-4xl leading-tight tracking-tight text-canvas-paper-text mb-8 font-serif-editorial"
          style={{ fontFamily: (FONT_BY_KEY[settings.font_family] || FONT_BY_KEY.cormorant).stack }}
          data-testid="manuscript-h1-title"
        >
          {(() => {
            // iter55f88 — Respect the project-wide "Numbered vs. Titled"
            // toggle. When Numbered is on, the h1 reads "Chapter 3"; when
            // Titled is on, the stored title takes over, minus any
            // "Chapter N — " prefix the story-bible outliner may have baked in.
            const label = chapterLabel(ch, project);
            return label.replace(/^Chapter \d+\s*[—-]\s*/i, '');
          })()}
        </h1>
        <div
          ref={proseRef}
          data-testid="manuscript-prose"
          className={`text-canvas-paper-text font-serif-novel relative ${(PARA_SPACING_OPTIONS.find(o => o.key === (settings.paragraph_spacing_key || 'normal')) || PARA_SPACING_OPTIONS[1]).spaceClass}`}
          style={{
            fontFamily:  (FONT_BY_KEY[settings.font_family] || FONT_BY_KEY.cormorant).stack,
            fontSize:    `${clampManuscriptSize(settings.font_size_px)}px`,
            fontWeight:  settings.font_weight === 'bold' ? 700 : 400,
            fontStyle:   settings.font_style === 'italic' ? 'italic' : 'normal',
            lineHeight:  settings.line_height,
            textAlign:   activeMode === 'draft' && settings.text_align === 'left' ? 'justify' : settings.text_align,
            hyphens:     activeMode === 'draft' ? 'auto' : 'manual',
            // iter55g11 — preserve pasted whitespace (line-breaks, multi-space)
            // so the browser doesn't collapse a multi-line paste into one long
            // run and force the writer to re-format after every clipboard op.
            whiteSpace:  'pre-wrap',
          }}
        >
          {/* ── Select-All flash badge ─────────────────────────────────────
              Floats above the prose when Ctrl+A spans multiple paragraphs.   */}
          {selectBadge !== null && (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center"
              data-testid="select-all-badge"
            >
              <span
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[13px] font-semibold text-primary shadow-sm backdrop-blur-sm"
                style={{ animation: 'fadeInDown 0.18s ease, fadeOut 0.3s ease 1.5s forwards' }}
              >
                <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="1" y="1" width="10" height="10" rx="1" />
                  <path d="M3.5 6h5M3.5 4h5M3.5 8h3" />
                </svg>
                {selectBadge} {selectBadge === 1 ? 'paragraph' : 'paragraphs'} selected
              </span>
            </div>
          )}
          {/* iter55g35 — Absolute-positioned suggestion overlays. One
              rectangle per line the anchor spans. Rectangles are
              coloured by suggestion kind (edit=amber, comment=sky)
              and clickable to open the inline popover. The layer is
              gated on `paragraphsEditable` because the read-only path
              already inlines <mark> highlights via HighlightedParagraph. */}
          {paragraphsEditable && overlays.length > 0 && (
            <div
              data-testid="manuscript-overlay-layer"
              className="pointer-events-none absolute inset-0 z-[1]"
              aria-hidden="true"
            >
              {overlays.map((o) =>
                o.rects.map((r, i) => {
                  const s = o.suggestion;
                  const isEdit = s.kind === 'edit';
                  const isPulse = pulseId === s.id;
                  const label =
                    isEdit
                      ? `Suggested edit by ${s.author_name || 'reviewer'}`
                      : `Comment by ${s.author_name || 'reviewer'}`;
                  return (
                    <div
                      key={`${o.id}-r${i}`}
                      data-testid={`manuscript-anchor-overlay-${s.id}`}
                      data-suggestion-id={s.id}
                      data-pulse={isPulse ? 'true' : undefined}
                      title={label}
                      // iter55g37 — Overlays MUST NOT capture pointer
                      // events. Doing so blocks cross-paragraph text
                      // selection whenever a mouse drag crosses an
                      // overlay rectangle. Interaction with the
                      // suggestion happens through the SuggestionsDrawer
                      // (Cmd/Ctrl+J also jumps and pulses the target).
                      className={
                        'pointer-events-none absolute rounded-[2px] '
                        + (isEdit
                          ? 'bg-amber-300/25 border-b-2 border-amber-500/70'
                          : 'bg-sky-300/25 border-b-2 border-sky-500/70')
                        + (isPulse ? ' suggestion-overlay-pulse' : '')
                      }
                      style={{
                        top:    `${r.top}px`,
                        left:   `${r.left}px`,
                        width:  `${r.width}px`,
                        height: `${r.height}px`,
                      }}
                    />
                  );
                }),
              )}
              {/* iter55h — Hover popover with the full suggestion body.
                  Anchored to the hovered rect. Pointer-events none so
                  hovering the popover itself does not re-trigger the
                  mousemove hit-test. */}
              {hover && (() => {
                const s = hover.s;
                const isEdit = s.kind === 'edit';
                const top  = (hover.rect.top ?? 0) + (hover.rect.height ?? 0) + 6;
                const left = Math.max(4, (hover.rect.left ?? 0));
                return (
                  <div
                    data-testid={`manuscript-hover-popover-${s.id}`}
                    className={
                      'pointer-events-none absolute z-[3] max-w-[340px] rounded-md border p-2.5 text-[13.5px] shadow-lg backdrop-blur-sm ' +
                      (isEdit
                        ? 'border-amber-500/60 bg-amber-50/95 text-amber-950 dark:bg-amber-900/80 dark:text-amber-50'
                        : 'border-sky-500/60 bg-sky-50/95 text-sky-950 dark:bg-sky-900/80 dark:text-sky-50')
                    }
                    style={{ top: `${top}px`, left: `${left}px` }}
                  >
                    <div className="mb-1 flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-wider opacity-80">
                      {s.author_picture ? (
                        <img
                          src={s.author_picture}
                          alt=""
                          className="h-4 w-4 rounded-full border border-current/30 object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <span className={
                          'inline-flex h-4 w-4 items-center justify-center rounded-full text-[8.5px] font-bold ' +
                          (isEdit ? 'bg-amber-500 text-white' : 'bg-sky-500 text-white')
                        }>
                          {(s.author_name || 'A').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span>{isEdit ? 'Suggested edit' : 'Comment'}</span>
                      <span>·</span>
                      <span>{s.author_name || 'reviewer'}</span>
                    </div>
                    {isEdit && s.proposed_text ? (
                      <div className="space-y-1">
                        <div className="line-through opacity-60">
                          &ldquo;{(s.anchor_text || '').slice(0, 180)}&rdquo;
                        </div>
                        <div className="font-medium">
                          &rarr; &ldquo;{(s.proposed_text || '').slice(0, 260)}&rdquo;
                        </div>
                        {s.note && (
                          <div className="mt-1 border-t border-current/20 pt-1 text-[13px] italic opacity-90">
                            {s.note}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">
                        {(s.note || '(no comment text)').slice(0, 500)}
                      </div>
                    )}
                    <div className="mt-1.5 text-[9.5px] opacity-60">
                      Cmd/Ctrl+J to open in Suggestions drawer
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {isStreamingThis ? (
            <StreamingProse text={streamingText} />
          ) : (
            <>
              {isEmpty ? (
                paragraphsEditable ? (
                  <BlankChapterCanvas
                    ref={blankCanvasRef}
                    chapterId={ch.id}
                    hover={blankHover}
                    setHover={setBlankHover}
                    canGenerate={canGenerate}
                    canAiWriteChapters={canAiWriteChapters}
                    onGenerate={onGenerateChapter}
                    onSave={handleBlankCanvasSave}
                  />
                ) : (
                  <EmptyState
                    onGenerate={() => onGenerateChapter(ch.id)}
                    canGenerate={canGenerate && canAiWriteChapters}
                  />
                )
              ) : (
                paragraphs.map((p, i) => (
                  <HighlightedParagraph
                    key={p.id}
                    paragraph={p}
                    index={i}
                    suggestions={pendingSuggestions}
                    editable={paragraphsEditable}
                    firstLetterDrop={i === 0}
                    settings={settings}
                    aiShare={aiShare}
                    onBlur={(e) => {
                      // Skip saving if a cross-paragraph delete is in progress —
                      // the blur fires because we called sel.removeAllRanges()
                      // and the save would race with crossParaDelete, writing
                      // stale paragraph content back at the wrong index.
                      // Also skip if a manual save already called saveParagraphEdit
                      // directly (manualSaveActiveRef) — the onBlur here is caused
                      // by EditorPanel's active.blur() and would be a duplicate save.
                      if (!crossParaDeleteActiveRef.current && !manualSaveActiveRef.current) {
                        saveParagraphEdit(i, e.currentTarget.innerText);
                      }
                      onDirtyClear?.();
                    }}
                    onInput={onDirty}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        e.currentTarget.innerText = p.text;
                        e.currentTarget.blur();
                      }
                      /* iter55f95 — Word-processor Tab behaviour.
                       * The browser's default is to move focus to the
                       * next `<p contentEditable>` sibling, which the
                       * user experienced as "jumping to the next
                       * paragraph". Intercept and insert 4 non-breaking
                       * spaces at the caret instead. Non-breaking so
                       * the browser preserves them (default `<p>`
                       * whitespace collapses runs of regular spaces). */
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        const sel = window.getSelection();
                        if (!sel || sel.rangeCount === 0) return;
                        const range = sel.getRangeAt(0);
                        range.deleteContents();
                        range.insertNode(document.createTextNode('\u00a0\u00a0\u00a0\u00a0'));
                        range.collapse(false);
                        sel.removeAllRanges();
                        sel.addRange(range);
                      }
                      /* iter55f95 — Backspace at paragraph start merges
                       * with the previous paragraph (word-processor
                       * behaviour). Detects "caret at very start" by
                       * measuring the pre-caret range length; matches
                       * inside marks / nested spans too. */
                      if (e.key === 'Backspace' && i > 0 && mergeParagraphUp) {
                        const sel = window.getSelection();
                        if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;
                        const preCaret = document.createRange();
                        preCaret.selectNodeContents(e.currentTarget);
                        preCaret.setEnd(sel.anchorNode, sel.anchorOffset);
                        if (preCaret.toString().length === 0) {
                          e.preventDefault();
                          mergeParagraphUp(i, e.currentTarget.innerText);
                        }
                      }
                    }}
                    onAnchorClick={onAnchorClick}
                  />
                ))
              )}
              {suggestion && (
                <ClaudeSuggestion
                  text={suggestion.text}
                  mode={suggestion.mode}
                  onAccept={onAcceptSuggestion}
                  onReject={onRejectSuggestion}
                />
              )}
              {!suggestion && !isEmpty && (
                <>
                  <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-panel-border/50 pt-4" data-testid="editor-inline-tone-row">
                    <span className="text-[13px] uppercase tracking-wider text-muted-foreground">Try again with a different tone:</span>
                    <InlineToneButton kind="darker"  label="Darker"       Icon={Moon} busy={toneBusy} onClick={() => askToneShift('darker')}  disabled={isDemo || !canGenerate} enough={enoughForTone} cost={toneCost} testid="editor-inline-tone-darker"  />
                    <InlineToneButton kind="lighter" label="Lighter"      Icon={Sun}  busy={toneBusy} onClick={() => askToneShift('lighter')} disabled={isDemo || !canGenerate} enough={enoughForTone} cost={toneCost} testid="editor-inline-tone-lighter" />
                    <InlineToneButton kind="action"  label="More action"  Icon={Zap}  busy={toneBusy} onClick={() => askToneShift('action')}  disabled={isDemo || !canGenerate} enough={enoughForTone} cost={toneCost} testid="editor-inline-tone-action" />
                    <span className="ml-auto font-mono-stat text-[13px] text-muted-foreground">
                      ~{fmtTokens(toneCost)} tokens · {fmtTokens(remaining)} left
                    </span>
                  </div>
                  {paragraphsEditable ? (
                    <p className="mt-2 text-[13px] text-foreground/70" data-testid="editor-edit-hint">
                      Click any paragraph to edit ·
                      <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">Cmd/Ctrl</kbd>+
                      <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">click</kbd> selects the whole chapter ·
                      <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">Ctrl+A</kbd> selects all paragraphs ·
                      drag to highlight text for AI suggestions ·
                      <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">Cmd/Ctrl</kbd>+
                      <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">Enter</kbd> save ·
                      <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">Esc</kbd> cancel
                      {suggestionCount > 0 && (
                        <>
                          {' · '}
                          <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">Cmd/Ctrl</kbd>+
                          <kbd className="rounded border border-panel-border bg-panel px-1 py-0.5 font-mono-stat text-[13px]">J</kbd> next suggestion
                        </>
                      )}
                    </p>
                  ) : (
                    <p className="text-ink-soft/80">
                      <span className="caret-blink" />
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {/* ── Close floating paper card ── */}
        </div>
      </div>
    </div>
  );
}
